import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { Grid } from './Grid';
import { GlowingFigure } from './GlowingFigure';

interface ShakeConfig {
    intensity: number;
    decay: number;
    duration: number;
    isShaking: boolean;
    startTime: number;
    originalCameraPos: THREE.Vector3;
    originalCameraRot: THREE.Euler;
    originalTarget: THREE.Vector3;
    originalBloomStrength: number;
    impactTime: number;
    lastShakeTime: number;
    zoomDistance: number;
}

interface SceneParams {
    moveSpeed: number;
    rippleSpeed: number;
    rippleIntensity: number;
    glowIntensity: number;
    waterOpacity: number;
    fogDensity: number;
}

export class Scene {
    public scene: THREE.Scene;
    public renderer!: THREE.WebGLRenderer;
    public camera!: THREE.PerspectiveCamera;
    public grid!: Grid;
    public figure!: GlowingFigure;
    private clock: THREE.Clock;
    private composer!: EffectComposer;
    private bloomPass!: UnrealBloomPass;
    private chromaticPass!: ShaderPass;
    private shakeConfig: ShakeConfig;
    public params: SceneParams;
    private autoMoving: boolean = false;
    private onAutoMoveComplete?: () => void;
    private animationFrameId?: number;
    private configElement: HTMLDivElement | null = null;
    private showConfig: boolean = false;

    constructor() {
        this.scene = new THREE.Scene();
        this.clock = new THREE.Clock();
        
        this.shakeConfig = {
            intensity: 1.0,
            decay: 0.85,
            duration: 1000,
            isShaking: false,
            startTime: 0,
            originalCameraPos: new THREE.Vector3(),
            originalCameraRot: new THREE.Euler(),
            originalTarget: new THREE.Vector3(),
            originalBloomStrength: 0.8,
            impactTime: 0,
            lastShakeTime: 0,
            zoomDistance: 5.0
        };

        this.params = {
            moveSpeed: 0.8,
            rippleSpeed: 12.0,
            rippleIntensity: 1.5,
            glowIntensity: 3.0,
            waterOpacity: 0.85,
            fogDensity: 0.003
        };

        this.setupCamera();
        this.setupRenderer();
        this.setupPostProcessing();
        this.setupFog();
        this.setupComponents();
        this.setupGUI();
        this.animate();
    }

    private setupGUI(): void {
        // Create config element
        this.configElement = document.createElement('div');
        this.configElement.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 10px;
            font-family: monospace;
            font-size: 12px;
            display: none;
            z-index: 1000;
        `;
        document.body.appendChild(this.configElement);
        this.updateConfigDisplay();
    }

    private updateConfigDisplay(): void {
        if (!this.configElement) return;
        this.configElement.innerHTML = `
            moveSpeed: ${this.params.moveSpeed.toFixed(2)}<br>
            rippleSpeed: ${this.params.rippleSpeed.toFixed(2)}<br>
            rippleIntensity: ${this.params.rippleIntensity.toFixed(2)}<br>
            glowIntensity: ${this.params.glowIntensity.toFixed(2)}<br>
            waterOpacity: ${this.params.waterOpacity.toFixed(2)}<br>
            fogDensity: ${this.params.fogDensity.toFixed(3)}
        `;
    }

    public toggleConfig(show: boolean): void {
        this.showConfig = show;
        if (this.configElement) {
            this.configElement.style.display = show ? 'block' : 'none';
        }
    }

    private setupCamera(): void {
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 2000);
        this.camera.position.set(10, 15, 0);
        this.camera.lookAt(0, 2, -50);
    }

    private setupRenderer(): void {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            powerPreference: "high-performance"
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setClearColor(0x000000);
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.2;
    }

    private setupPostProcessing(): void {
        this.composer = new EffectComposer(this.renderer);
        
        const renderPass = new RenderPass(this.scene, this.camera);
        this.composer.addPass(renderPass);

        this.bloomPass = new UnrealBloomPass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            0.8,
            0.75,
            0.35
        );
        this.composer.addPass(this.bloomPass);

        // Chromatic aberration shader
        const chromaticAberrationShader = {
            uniforms: {
                "tDiffuse": { value: null },
                "distortion": { value: 0.0 }
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D tDiffuse;
                uniform float distortion;
                varying vec2 vUv;
                void main() {
                    vec2 center = vec2(0.5);
                    vec2 coord = vUv;
                    vec2 dir = coord - center;
                    float dist = length(dir);
                    
                    vec2 offset = dir * (dist * dist * distortion);
                    
                    vec4 cr = texture2D(tDiffuse, coord - offset);
                    vec4 cg = texture2D(tDiffuse, coord);
                    vec4 cb = texture2D(tDiffuse, coord + offset);
                    
                    gl_FragColor = vec4(cr.r, cg.g, cb.b, 1.0);
                }
            `
        };

        this.chromaticPass = new ShaderPass(chromaticAberrationShader);
        this.chromaticPass.uniforms.distortion.value = 0.0;
        this.composer.addPass(this.chromaticPass);
    }

    private setupFog(): void {
        this.scene.fog = new THREE.FogExp2(0x000000, 0.002);
    }

    private setupComponents(): void {
        // Initialize water surface
        this.grid = new Grid();
        this.grid.addToScene(this.scene);

        // Initialize figure
        this.figure = new GlowingFigure();
        this.figure.addToScene(this.scene);
    }

    public startShake(intensity: number = 1.0, duration: number = 1000, zoomDistance: number = 5.0): void {
        if (!this.shakeConfig.isShaking) {
            this.shakeConfig.intensity = intensity;
            this.shakeConfig.duration = duration;
            this.shakeConfig.zoomDistance = zoomDistance;
            this.shakeConfig.startTime = Date.now();
            this.shakeConfig.impactTime = Date.now();
            this.shakeConfig.lastShakeTime = Date.now();
            this.shakeConfig.isShaking = true;
            this.shakeConfig.originalCameraPos.copy(this.camera.position);
            this.shakeConfig.originalTarget.copy(this.figure.basePosition);
            this.shakeConfig.originalBloomStrength = this.bloomPass.strength;

            this.camera.lookAt(this.shakeConfig.originalTarget);
        }
    }

    public startAutoMove(onComplete?: () => void): void {
        this.autoMoving = true;
        this.onAutoMoveComplete = onComplete;
        this.params.moveSpeed = 0.5;
    }

    private updateCamera(): void {
        if (!this.figure) return;

        const currentTime = Date.now();

        // Handle shake effect
        if (this.shakeConfig.isShaking) {
            const elapsed = currentTime - this.shakeConfig.startTime;
            const progress = elapsed / this.shakeConfig.duration;

            if (elapsed < this.shakeConfig.duration) {
                const shakeIntensity = this.shakeConfig.intensity * 
                    Math.exp(-elapsed * 0.003) * 
                    (1 + Math.sin(elapsed * 0.05));
                
                const zoomIntensity = Math.sin(progress * Math.PI) * this.shakeConfig.zoomDistance;
                
                const directionToFigure = new THREE.Vector3()
                    .subVectors(this.figure.basePosition, this.shakeConfig.originalCameraPos)
                    .normalize();
                
                const shakeOffset = new THREE.Vector3(
                    Math.sin(elapsed * 0.1) * shakeIntensity * 0.8,
                    Math.sin(elapsed * 0.08) * shakeIntensity * 0.6,
                    Math.sin(elapsed * 0.12) * shakeIntensity * 0.8
                );
                
                this.camera.position.copy(this.shakeConfig.originalCameraPos)
                    .add(shakeOffset)
                    .add(directionToFigure.multiplyScalar(zoomIntensity));
                
                const targetOffset = new THREE.Vector3(
                    Math.sin(elapsed * 0.07) * shakeIntensity * 0.2,
                    Math.sin(elapsed * 0.09) * shakeIntensity * 0.2,
                    Math.sin(elapsed * 0.11) * shakeIntensity * 0.2
                );
                const shakeTarget = this.shakeConfig.originalTarget.clone().add(targetOffset);
                this.camera.lookAt(shakeTarget);
                
                const effectIntensity = Math.min(1.0, shakeIntensity * 0.4);
                this.bloomPass.strength = this.shakeConfig.originalBloomStrength + effectIntensity * 0.5;
                this.chromaticPass.uniforms.distortion.value = effectIntensity * 0.02;
            } else {
                this.shakeConfig.isShaking = false;
                this.camera.position.copy(this.shakeConfig.originalCameraPos);
                this.camera.lookAt(this.figure.basePosition);
                this.bloomPass.strength = this.shakeConfig.originalBloomStrength;
                this.chromaticPass.uniforms.distortion.value = 0.0;
            }
            return;
        }

        // Handle auto movement
        if (this.autoMoving) {
            const directionToFigure = new THREE.Vector3()
                .subVectors(this.figure.basePosition, this.camera.position)
                .normalize();

            this.camera.position.add(
                directionToFigure.multiplyScalar(this.params.moveSpeed)
            );

            const distanceToFigure = this.camera.position.distanceTo(this.figure.basePosition);
            if (distanceToFigure <= 15) {
                this.autoMoving = false;
                if (this.onAutoMoveComplete) {
                    this.onAutoMoveComplete();
                }
            }
        }

        // Enhanced floating motion
        const time = this.clock.getElapsedTime();
        this.camera.position.y = 15 + Math.sin(time * 0.3) * 1.0;
        
        // Look at figure
        this.camera.lookAt(this.figure.basePosition);
    }

    private animate(): void {
        this.animationFrameId = requestAnimationFrame(() => this.animate());
        
        const currentTime = Date.now();

        // Update components
        this.figure.update(currentTime);
        this.grid.update(currentTime);
        this.updateCamera();
        
        // Update view-dependent effects
        this.grid.updateCameraPosition(this.camera);
        this.figure.updateViewPosition(this.camera);
        
        // Update config display if visible
        if (this.showConfig) {
            this.updateConfigDisplay();
        }
        
        // Render with post-processing
        this.composer.render();
    }

    public onResize(): void {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        
        this.renderer.setSize(width, height);
        this.composer.setSize(width, height);
    }

    public dispose(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        
        this.grid?.dispose();
        this.figure?.dispose();
        
        this.scene.traverse((object: THREE.Object3D) => {
            if (object instanceof THREE.Mesh) {
                object.geometry.dispose();
                if (object.material instanceof THREE.Material) {
                    object.material.dispose();
                } else if (Array.isArray(object.material)) {
                    object.material.forEach(material => material.dispose());
                }
            }
        });
        
        this.renderer?.dispose();
        this.composer?.dispose();
    }
}
